/**
 * ButtonShowcase — fw-choi UI 컴포넌트 쇼케이스(데모). 실제 업무 화면 아님.
 * 【역할】 공통 컴포넌트/패턴의 사용 예시를 한 화면에 모아 보여준다.
 */
import { Card, Button, Space, Dropdown, FloatButton, Typography, Row, Col } from 'antd'
import {
  DownloadOutlined,
  PoweroffOutlined,
  SearchOutlined,
  PlusOutlined,
  CommentOutlined,
  CustomerServiceOutlined,
  QuestionCircleOutlined,
  SettingOutlined,
  EditOutlined
} from '@ant-design/icons'
import PageContainer from '@/components/common/PageContainer'
import { useLabel } from '@/framework/useLabel'

const { Title } = Typography

const menuItems = [
  { key: '1', label: 'One' },
  { key: '2', label: 'Two' },
  { key: '3', label: 'Three' },
  { type: 'divider' as const },
  { key: '4', label: 'Four', danger: true }
]

const cardBodyStyle = { padding: 10 }

/**
 * ============================================================================
 * ButtonShowcase — antd Button / 액션 트리거 카탈로그
 * ============================================================================
 *
 * 【역할】 antd Button 의 type/size/shape/loading/disabled variant 와
 *         Dropdown / FloatButton 등 액션 트리거 UI 를 한 화면에 나열.
 *
 * 【시연 항목】
 *   - Types        — primary/default/dashed/text/link/danger/ghost/disabled
 *   - Icons        — icon prop / loading / shape (circle, round)
 *   - Sizes/Block  — large/default/small + block
 *   - Space.Compact— 그룹화된 버튼 묶음
 *   - Dropdown     — Dropdown / Dropdown.Button (split)
 *   - FloatButton  — 단독 / Group / BackTop
 *
 * 【사용처】 router/index.tsx — `{ path: '/showcase/button' }` (lazy)
 * 【포팅 원본】 자체 작성 (vue-spring-fw V12 Button 카탈로그 참고)
 * 【의존성】 antd Button/Space/Dropdown/FloatButton/Typography, @ant-design/icons
 * 【참고】 https://ant.design/components/button
 */
export default function ButtonShowcase() {
  const { t } = useLabel()
  return (
    <PageContainer style={{ overflow: 'hidden' }}>
      <div style={{ flex: '0 0 auto', marginBottom: 8 }}>
        <Title level={4} style={{ margin: 0 }}>
          {t('LBL_BUTTON_COMPONENTS', 'Button 컴포넌트')}
        </Title>
      </div>

      <div
        style={{
          flex: 1,
          minHeight: 0,
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gridTemplateRows: '1fr 1fr',
          gap: 10,
          overflow: 'hidden'
        }}
      >
        <Card size="small" title="Types" styles={{ body: cardBodyStyle }}>
          <Space wrap size={[4, 4]}>
            <Button type="primary">Primary</Button>
            <Button>Default</Button>
            <Button type="dashed">Dashed</Button>
            <Button type="text">Text</Button>
            <Button type="link">Link</Button>
            <Button type="primary" danger>
              Danger
            </Button>
            <Button type="primary" ghost>
              Ghost
            </Button>
            <Button disabled>Disabled</Button>
          </Space>
        </Card>

        <Card size="small" title="Icons / Loading / Shapes" styles={{ body: cardBodyStyle }}>
          <Space wrap size={[4, 4]}>
            <Button icon={<SearchOutlined />}>Search</Button>
            <Button type="primary" icon={<DownloadOutlined />}>
              Download
            </Button>
            <Button icon={<PoweroffOutlined />} loading>
              Loading
            </Button>
            <Button shape="circle" icon={<PlusOutlined />} />
            <Button shape="round" type="primary">
              Round
            </Button>
            <Button shape="circle" icon={<EditOutlined />} type="primary" />
            <Button shape="round" icon={<SearchOutlined />}>
              Search Round
            </Button>
          </Space>
        </Card>

        <Card size="small" title="Sizes / Block" styles={{ body: cardBodyStyle }}>
          <Space direction="vertical" style={{ width: '100%' }} size={6}>
            <Space size={4}>
              <Button size="large" type="primary">
                Large
              </Button>
              <Button type="primary">Default</Button>
              <Button size="small" type="primary">
                Small
              </Button>
            </Space>
            <Button block type="primary">
              Block Button
            </Button>
          </Space>
        </Card>

        <Card size="small" title="Space.Compact" styles={{ body: cardBodyStyle }}>
          <Space direction="vertical" size={6}>
            <Space.Compact>
              <Button>Left</Button>
              <Button>Middle</Button>
              <Button>Right</Button>
            </Space.Compact>
            <Space.Compact>
              <Button type="primary">Save</Button>
              <Button type="primary">Save As</Button>
              <Button type="primary">Save All</Button>
            </Space.Compact>
            <Space.Compact block>
              <Button type="primary" icon={<EditOutlined />}>
                Edit
              </Button>
              <Button type="primary" icon={<DownloadOutlined />}>
                Download
              </Button>
            </Space.Compact>
          </Space>
        </Card>

        <Card size="small" title="Dropdown / Dropdown.Button" styles={{ body: cardBodyStyle }}>
          <Space wrap size={[4, 4]}>
            <Dropdown menu={{ items: menuItems }}>
              <Button>Dropdown</Button>
            </Dropdown>
            <Dropdown.Button menu={{ items: menuItems }}>Dropdown Button</Dropdown.Button>
            <Dropdown.Button menu={{ items: menuItems }} type="primary">
              Primary Split
            </Dropdown.Button>
            <Dropdown.Button menu={{ items: menuItems }} danger icon={<SettingOutlined />}>
              Danger Split
            </Dropdown.Button>
          </Space>
        </Card>

        <Card size="small" title="FloatButton Variants" styles={{ body: cardBodyStyle }}>
          <Row gutter={[8, 8]}>
            <Col span={24}>
              <Title level={5} style={{ marginTop: 0, marginBottom: 4 }}>
                {t('LBL_FLOAT_BTN_SET_HINT', '우측 하단 FloatButton 세트 확인')}
              </Title>
              <p style={{ color: '#666', fontSize: 12, margin: 0 }}>
                {t(
                  'LBL_FLOAT_BTN_SET_DESC',
                  '기본 FloatButton, Group, BackTop 세 종류를 동시에 렌더링합니다.'
                )}
              </p>
            </Col>
          </Row>
        </Card>
      </div>

      <FloatButton
        icon={<QuestionCircleOutlined />}
        tooltip={t('BTN_HELP', '도움말')}
        style={{ insetInlineEnd: 100, insetBlockEnd: 100 }}
      />
      <FloatButton.Group
        trigger="hover"
        type="primary"
        style={{ insetInlineEnd: 40, insetBlockEnd: 100 }}
        icon={<CustomerServiceOutlined />}
      >
        <FloatButton tooltip={t('LBL_COMMENT', '댓글')} icon={<CommentOutlined />} />
        <FloatButton tooltip={t('BTN_SETTINGS', '설정')} icon={<SettingOutlined />} />
      </FloatButton.Group>
      <FloatButton.BackTop visibilityHeight={0} />
    </PageContainer>
  )
}
